import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  DryRunUpstreamErrorPolicyRuleRequest,
  DryRunUpstreamErrorPolicyRuleResponse,
  UpstreamErrorObservation,
  UpstreamErrorPolicyRule,
  UpstreamErrorPolicyRuleInput,
  UpstreamErrorReviewStatus,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import {
  createUpstreamErrorPolicyRule,
  dryRunUpstreamErrorPolicyRule,
  ignoreUpstreamErrorObservation,
  listUpstreamErrorObservations,
  listUpstreamErrorPolicyRules,
  updateUpstreamErrorPolicyRule,
} from './upstreamErrorPolicy.ts';

const UPSTREAM_ERROR_POLICY_KEY = ['upstream-error-policy'] as const;
const OBSERVATIONS_KEY = [...UPSTREAM_ERROR_POLICY_KEY, 'observations'] as const;
const RULES_KEY = [...UPSTREAM_ERROR_POLICY_KEY, 'rules'] as const;

export function useUpstreamErrorObservations(
  reviewStatus: UpstreamErrorReviewStatus | 'all',
): UseQueryResult<UpstreamErrorObservation[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...OBSERVATIONS_KEY, reviewStatus] as const,
    queryFn: ({ signal }) => listUpstreamErrorObservations({ reviewStatus, limit: 100 }, fetchImpl, signal),
  });
}

export function useUpstreamErrorPolicyRules(): UseQueryResult<UpstreamErrorPolicyRule[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: RULES_KEY,
    queryFn: ({ signal }) => listUpstreamErrorPolicyRules(fetchImpl, signal),
  });
}

export function useDryRunUpstreamErrorPolicyRule(): UseMutationResult<
  DryRunUpstreamErrorPolicyRuleResponse,
  Error,
  DryRunUpstreamErrorPolicyRuleRequest
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: (body) => dryRunUpstreamErrorPolicyRule(body, fetchImpl),
  });
}

export function useCreateUpstreamErrorPolicyRule(): UseMutationResult<
  UpstreamErrorPolicyRule,
  Error,
  UpstreamErrorPolicyRuleInput
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createUpstreamErrorPolicyRule(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UPSTREAM_ERROR_POLICY_KEY });
    },
  });
}

export function useUpdateUpstreamErrorPolicyRule(): UseMutationResult<
  UpstreamErrorPolicyRule,
  Error,
  { readonly id: string; readonly body: UpstreamErrorPolicyRuleInput }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => updateUpstreamErrorPolicyRule(id, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UPSTREAM_ERROR_POLICY_KEY });
    },
  });
}

export function useIgnoreUpstreamErrorObservation(): UseMutationResult<
  UpstreamErrorObservation,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fingerprint) => ignoreUpstreamErrorObservation(fingerprint, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UPSTREAM_ERROR_POLICY_KEY });
    },
  });
}
